/**
 * VastuChart - Visual representation of directions based on planetary positions
 */

import React from 'react';
import { Box, Typography, Sheet, Chip, Select, Option } from '@mui/joy';
import { PlanetName } from '../utils/astroEngine';
import { VastuDirection, getPlanetDirection, getPlanetDirections, PLANET_DIRECTIONS, LIFE_AREA_HOUSES } from '../utils/vastuUtils';

interface VastuChartProps {
  placements: Record<string, number>;
  houseLords: Record<number, PlanetName>;
  ascSign: number;
  functionalBenefics?: Set<PlanetName>;
  exaltedPlanets?: Record<string, boolean>;
  shadbalaScores?: Partial<Record<PlanetName, number>>;
  blendedScores?: Partial<Record<PlanetName, number>>;
}

const DIRECTION_COLORS: Record<VastuDirection, string> = {
  'East': '#f59e0b',        // Amber (Sun)
  'Southeast': '#ec4899',   // Pink (Venus)
  'South': '#dc2626',       // Red (Mars)
  'Southwest': '#6366f1',   // Indigo (Saturn/Rahu)
  'West': '#8b5cf6',        // Purple (Saturn)
  'Northwest': '#3b82f6',   // Blue (Moon)
  'North': '#10b981',       // Green (Mercury)
  'Northeast': '#f97316',   // Orange (Jupiter/Ketu)
  'Center': '#6b7280',      // Gray
};

const DIRECTION_ELEMENTS: Record<VastuDirection, { element: string; tips: string[] }> = {
  'East': { 
    element: '🌅 Fire/Sun', 
    tips: ['Keep open & clutter-free', 'Use light colors', 'Place windows here']
  },
  'Southeast': { 
    element: '🔥 Fire', 
    tips: ['Ideal for kitchen', 'Use red/orange', 'Electrical items here']
  },
  'South': { 
    element: '⚡ Fire/Energy', 
    tips: ['Keep heavy objects', 'Use red/brown', 'Master bedroom ideal']
  },
  'Southwest': { 
    element: '🪨 Earth', 
    tips: ['Heaviest corner', 'Store valuables', 'Avoid water features']
  },
  'West': { 
    element: '💧 Water/Metal', 
    tips: ['Study/dining room', 'Use white/gray', 'Storage area']
  },
  'Northwest': { 
    element: '💨 Air', 
    tips: ['Guest room ideal', 'Use light colors', 'Good for movement']
  },
  'North': { 
    element: '💰 Water/Wealth', 
    tips: ['Cash/safe here', 'Use green/blue', 'Keep open']
  },
  'Northeast': { 
    element: '🕉️ Water/Spirit', 
    tips: ['Prayer/meditation', 'Most sacred', 'Keep clean & open']
  },
  'Center': { 
    element: '🌌 Space', 
    tips: ['Keep empty/open', 'Avoid heavy items', 'Brahmasthan']
  },
};

export default function VastuChart(props: VastuChartProps) {
  const {
    placements,
    houseLords,
    functionalBenefics,
    exaltedPlanets,
    shadbalaScores,
    blendedScores,
  } = props;

  const [houseFacing, setHouseFacing] = React.useState<VastuDirection>('North');

  // Direction order for rotation
  const directionOrder: VastuDirection[] = [
    'North', 'Northeast', 'East', 'Southeast',
    'South', 'Southwest', 'West', 'Northwest'
  ];

  // Map facing direction → grid rotation so the facing direction appears at top-centre
  const FACING_TO_ROTATION: Record<VastuDirection, number> = {
    'North': 0, 'Northwest': 1, 'West': 2, 'Southwest': 3,
    'South': 4, 'Southeast': 5, 'East': 6, 'Northeast': 7, 'Center': 0,
  };
  const rotation = FACING_TO_ROTATION[houseFacing];

  // Build direction data based on planets and their houses
  const directionCounts: Record<VastuDirection, {
    houses: number[];
    planets: Set<PlanetName>;
    isOccupant: boolean[];
    totalBlended: number;
    lifeAreas: Set<string>;
  }> = {
    'East': { houses: [], planets: new Set(), isOccupant: [], totalBlended: 0, lifeAreas: new Set() },
    'Southeast': { houses: [], planets: new Set(), isOccupant: [], totalBlended: 0, lifeAreas: new Set() },
    'South': { houses: [], planets: new Set(), isOccupant: [], totalBlended: 0, lifeAreas: new Set() },
    'Southwest': { houses: [], planets: new Set(), isOccupant: [], totalBlended: 0, lifeAreas: new Set() },
    'West': { houses: [], planets: new Set(), isOccupant: [], totalBlended: 0, lifeAreas: new Set() },
    'Northwest': { houses: [], planets: new Set(), isOccupant: [], totalBlended: 0, lifeAreas: new Set() },
    'North': { houses: [], planets: new Set(), isOccupant: [], totalBlended: 0, lifeAreas: new Set() },
    'Northeast': { houses: [], planets: new Set(), isOccupant: [], totalBlended: 0, lifeAreas: new Set() },
    'Center': { houses: [], planets: new Set(), isOccupant: [], totalBlended: 0, lifeAreas: new Set() },
  };

  // Track which planets have already had their blended score counted
  // (prevents double-count when a planet rules multiple directions AND lords an empty house)
  const alreadyCountedBlended = new Set<PlanetName>();

  // Add all planets to their natural Vastu directions
  // And track which houses they occupy
  Object.entries(placements).forEach(([planetName, houseNum]) => {
    const planet = planetName as PlanetName;
    const directions = getPlanetDirections(planet);
    
    // Add planet to all its associated directions
    directions.forEach(direction => {
      directionCounts[direction].planets.add(planet);
      directionCounts[direction].houses.push(houseNum);
      directionCounts[direction].isOccupant.push(true); // Planet is in a house
      
      // Add blended score (retain sign - can be positive or negative)
      if (blendedScores?.[planet] !== undefined) {
        directionCounts[direction].totalBlended += blendedScores[planet];
      }
      
      // Map house to life areas
      for (const [lifeArea, houses] of Object.entries(LIFE_AREA_HOUSES)) {
        if (houses.includes(houseNum)) {
          directionCounts[direction].lifeAreas.add(lifeArea);
        }
      }
    });
    alreadyCountedBlended.add(planet); // mark planet's blended as counted
  });

  // Add house lords for empty houses
  Array.from({ length: 12 }, (_, i) => i + 1).forEach(houseNum => {
    // Check if this house has any occupants
    const hasOccupants = Object.values(placements).includes(houseNum);
    
    if (!hasOccupants) {
      const lord = houseLords[houseNum];
      if (lord) {
        const directions = getPlanetDirections(lord);
        
        // Add lord to all its associated directions
        directions.forEach(direction => {
          // Only add house number if the lord isn't already shown for this house
          if (!directionCounts[direction].houses.includes(houseNum)) {
            directionCounts[direction].houses.push(houseNum);
            directionCounts[direction].planets.add(lord);
            directionCounts[direction].isOccupant.push(false); // Lord, not occupant
            
            // Add lord's blended score only if not already counted from placements loop
            if (!alreadyCountedBlended.has(lord) && blendedScores?.[lord] !== undefined) {
              directionCounts[direction].totalBlended += blendedScores[lord];
            }
            
            // Map house to life areas
            for (const [lifeArea, houses] of Object.entries(LIFE_AREA_HOUSES)) {
              if (houses.includes(houseNum)) {
                directionCounts[direction].lifeAreas.add(lifeArea);
              }
            }
          }
        });
        alreadyCountedBlended.add(lord); // prevent double-count if lord also in placements
      }
    }
  });

  // Compute direction strengths for the summary card
  const directionStrengths = (Object.entries(directionCounts) as [VastuDirection, typeof directionCounts[VastuDirection]][])
    .filter(([dir]) => dir !== 'Center')
    .map(([dir, data]) => ({ direction: dir, totalBlended: data.totalBlended }))
    .sort((a, b) => b.totalBlended - a.totalBlended);
  const top3 = directionStrengths.slice(0, 3);
  const bottom2 = directionStrengths.slice(-2).reverse();

  // Render directional box
  const renderDirectionBox = (direction: VastuDirection) => {
    const data = directionCounts[direction];
    const color = DIRECTION_COLORS[direction];
    
    // Get unique houses
    const uniqueHouses = Array.from(new Set(data.houses)).sort((a, b) => a - b);
    
    return (
      <Box
        sx={{
          border: '2px solid',
          borderColor: color,
          borderRadius: 'md',
          p: 1.5,
          bgcolor: data.planets.size > 0 ? `${color}15` : 'background.surface',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          minHeight: '100px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography level="title-sm" sx={{ fontWeight: 600, color }}>
            {direction}
          </Typography>
          
          {data.totalBlended !== 0 && (
            <Chip
              size="sm"
              color={data.totalBlended > 0 ? "primary" : "danger"}
              variant="soft"
              sx={{ fontSize: '0.7rem', fontWeight: 600 }}
            >
              {data.totalBlended > 0 ? '+' : ''}{Math.round(data.totalBlended)}%
            </Chip>
          )}
        </Box>
        
        {data.planets.size > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
            <Typography level="body-xs" sx={{ opacity: 0.7 }}>
              Planets:
            </Typography>
            {Array.from(data.planets).map(planet => {
              const isBenefic = functionalBenefics?.has(planet);
              return (
                <Chip
                  key={planet}
                  size="sm"
                  variant="soft"
                  color={isBenefic ? "success" : "danger"}
                  sx={{ fontSize: '0.75rem' }}
                >
                  {planet}
                </Chip>
              );
            })}
          </Box>
        )}
        
        {uniqueHouses.length > 0 && (
          <Typography level="body-sm">
            <Typography component="span" level="body-xs" sx={{ opacity: 0.7, mr: 0.5 }}>
              Houses:
            </Typography>
            {uniqueHouses.map(h => `H${h}`).join(', ')}
          </Typography>
        )}
        
        {data.lifeAreas.size > 0 && (
          <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {Array.from(data.lifeAreas).map(area => (
              <Chip
                key={area}
                size="sm"
                variant="outlined"
                color="neutral"
                sx={{ fontSize: '0.7rem' }}
              >
                {area}
              </Chip>
            ))}
          </Box>
        )}
        
        {/* Element and Tips */}
        <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography level="body-xs" sx={{ fontWeight: 600, mb: 0.5 }}>
            {DIRECTION_ELEMENTS[direction].element}
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2, fontSize: '0.7rem', opacity: 0.8 }}>
            {DIRECTION_ELEMENTS[direction].tips.map((tip, idx) => (
              <li key={idx}>{tip}</li>
            ))}
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Typography level="body-sm" sx={{ opacity: 0.7 }}>
          House entrance faces:
        </Typography>
        <Select
          size="sm"
          value={houseFacing}
          onChange={(_, val) => val && setHouseFacing(val as VastuDirection)}
          sx={{ minWidth: 140 }}
        >
          {(['North', 'Northeast', 'East', 'Southeast', 'South', 'Southwest', 'West', 'Northwest'] as VastuDirection[]).map(dir => (
            <Option key={dir} value={dir}>{dir}</Option>
          ))}
        </Select>
        <Typography level="body-xs" sx={{ opacity: 0.6, fontStyle: 'italic' }}>
          Rotates the grid to match your floor plan orientation
        </Typography>
      </Box>

      {/* 3x3 Grid representing Vastu directions */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 1.5,
          maxWidth: '800px',
          mb: 3,
        }}
      >
        {/* Map grid positions based on rotation */}
        {(() => {
          // Grid positions: [0,0]=NW, [0,1]=N, [0,2]=NE, [1,0]=W, [1,1]=Center, [1,2]=E, [2,0]=SW, [2,1]=S, [2,2]=SE
          const gridLayout: VastuDirection[][] = [
            ['Northwest', 'North', 'Northeast'],
            ['West', 'Center', 'East'],
            ['Southwest', 'South', 'Southeast']
          ];

          // Apply rotation to outer ring (center stays fixed)
          const rotateDirection = (dir: VastuDirection): VastuDirection => {
            if (dir === 'Center') return 'Center';
            const idx = directionOrder.indexOf(dir);
            const rotatedIdx = (idx - rotation + directionOrder.length) % directionOrder.length;
            return directionOrder[rotatedIdx];
          };

          return gridLayout.flat().map((dir, i) => (
            <React.Fragment key={i}>
              {renderDirectionBox(rotateDirection(dir))}
            </React.Fragment>
          ));
        })()}
      </Box>

      {/* Legend */}
      <Sheet
        variant="outlined"
        sx={{
          borderRadius: 'sm',
          p: 2,
          bgcolor: 'background.level1',
          maxWidth: '800px',
        }}
      >
        <Typography level="title-sm" sx={{ mb: 1 }}>
          Planetary Directions
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 1,
            fontSize: '13px',
          }}
        >
          {(Object.entries(PLANET_DIRECTIONS) as [PlanetName, VastuDirection[]][]).map(([planet, dirs]) => (
            <Box key={planet} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <strong>{planet}:</strong>
              <span style={{ color: DIRECTION_COLORS[dirs[0]] }}>
                {dirs.join(', ')}
              </span>
            </Box>
          ))}
        </Box>
        <Typography level="body-xs" sx={{ mt: 1.5, opacity: 0.7 }}>
          ★ Occupied houses use the best planet's direction (functional benefic → exalted → highest shadbala/blended %)
          <br />
          Empty houses use the house lord's direction
        </Typography>
      </Sheet>

      {/* Direction Strength Summary */}
      <Sheet
        variant="soft"
        sx={{ borderRadius: 'sm', p: 2, mt: 2, maxWidth: '800px', bgcolor: 'background.level1' }}
      >
        <Typography level="title-sm" sx={{ mb: 1 }}>Direction Strength Summary</Typography>
        <Box sx={{ mb: 1.5 }}>
          <Typography level="body-xs" sx={{ fontWeight: 600, mb: 0.5, color: 'success.700' }}>
            Strongest (focus energy here):
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {top3.map(({ direction, totalBlended }) => (
              <Chip key={direction} color="success" variant="soft" size="sm"
                sx={{ borderLeft: `3px solid ${DIRECTION_COLORS[direction]}` }}>
                {direction}: {totalBlended > 0 ? '+' : ''}{Math.round(totalBlended)}%
              </Chip>
            ))}
          </Box>
        </Box>
        <Box>
          <Typography level="body-xs" sx={{ fontWeight: 600, mb: 0.5, color: 'danger.600' }}>
            Weakest (needs attention):
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {bottom2.map(({ direction, totalBlended }) => (
              <Chip key={direction} color="danger" variant="soft" size="sm"
                sx={{ borderLeft: `3px solid ${DIRECTION_COLORS[direction]}` }}>
                {direction}: {totalBlended > 0 ? '+' : ''}{Math.round(totalBlended)}%
              </Chip>
            ))}
          </Box>
        </Box>
      </Sheet>
    </Box>
  );
}
